import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../../shared/api/erros';
import { chavesDeTriagem } from '../api/filaDeTriagem';
import { lerEmailDoTicket, leituraDaCaixaLigada } from './api/demoDoPipeline';

/**
 * Relê o e-mail aberto (corpo e anexos) com o parser e o agente de agora, e atualiza o detalhe. Existe para quem ajusta
 * a regra ou o prompt e quer ver o efeito num ticket já lido, sem esperar a coleta de e-mail novo. Só aparece com a
 * leitura da caixa ligada na API (mesmo gate da faixa da demo). A releitura atualiza os campos da máquina e preserva a
 * correção de uma pessoa.
 */
export function ReleituraDoEmail({ ticketId, emailId }: { ticketId: string; emailId: string }) {
  const clienteDeConsulta = useQueryClient();
  const ligada = useQuery({
    queryKey: ['demo', 'ligada'],
    queryFn: ({ signal }) => leituraDaCaixaLigada(signal),
    retry: false,
    staleTime: Infinity,
  });
  const releitura = useMutation({
    mutationFn: () => lerEmailDoTicket(ticketId, emailId),
    onSuccess: () => clienteDeConsulta.invalidateQueries({ queryKey: chavesDeTriagem.detalhe(ticketId) }),
  });

  if (ligada.data !== true) return null;

  return (
    <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      <Button
        variant="text"
        size="small"
        disabled={releitura.isPending}
        onClick={() => {
          releitura.mutate();
        }}
        sx={{ px: 0.5, minWidth: 0, fontSize: 12, textTransform: 'none' }}
      >
        {releitura.isPending ? 'Relendo…' : 'Reler este e-mail'}
      </Button>
      {releitura.isSuccess ? (
        <span role="status" aria-live="polite">
          {releitura.data.falha !== null
            ? `Não leu: ${releitura.data.falha}.`
            : releitura.data.naoEncontrado
              ? 'E-mail não encontrado na triagem.'
              : releitura.data.agenteRodou
                ? 'Relido, com o agente.'
                : 'Relido, só pela regra.'}
        </span>
      ) : null}
      {releitura.isError ? (
        <span role="alert">
          {releitura.error instanceof ErroDaApi && releitura.error.status === 409
            ? 'Há uma leitura em andamento; tente de novo em instantes.'
            : 'Não foi possível reler.'}
        </span>
      ) : null}
    </span>
  );
}
